import { ApolloQueryResult, DocumentNode, gql, QueryHookOptions } from '@apollo/client';
import { TemplateLocaleInput } from 'modules/graphql';
import { GqlInvalidContentReason } from 'modules/templateEditor/common/types';
import { useMemo } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';
import { isDefined, Nullable } from 'utils/tsHelpers';

export type ValidateContentQueryVariables = {
  templateId: number;
  locale?: Nullable<TemplateLocaleInput>;
  messageTypeId: number;
};

type ValidateContent = {
  templateId: number;
  locale: Nullable<TemplateLocaleInput>;
  errors: GqlInvalidContentReason[];
};

type ValidateContentQueryResponse = {
  validateContent: ValidateContent;
};

export type UseValidateContentQueryState = {
  isLoading: boolean;
  hasError: boolean;
  locale: Nullable<TemplateLocaleInput>;
  refetch: (
    variables?: Partial<ValidateContentQueryVariables> | undefined,
  ) => Promise<ApolloQueryResult<ValidateContentQueryResponse>>;
  validationFailures: GqlInvalidContentReason[];
};

export const validateContentQuery = gql`
  query ValidateContentQuery(
    $templateId: Long!
    $locale: TemplateLocaleInput
    $messageTypeId: Long!
  ) {
    validateContent(templateId: $templateId, locale: $locale, messageTypeId: $messageTypeId) {
      templateId
      locale {
        name
      }
      errors {
        ... on Unrenderable {
          fieldName
          msg
        }
        ... on UnrenderableWithIndex {
          fieldName
          msg
          index
        }
        ... on IllegalScript {
          fieldName
        }
        ... on InvalidEmail {
          fieldName
        }
        ... on JSInAnchorTag {
          fieldName
        }
        ... on JSInIframeTag {
          fieldName
        }
        ... on InvalidSubject {
          __typename
        }
        ... on MissingUnsubscribeUrl {
          __typename
        }
        ... on EmptyContent {
          fieldName
        }
        ... on InvalidMessageTypeId {
          __typename
        }
        ... on PreheaderTooLong {
          __typename
        }
        ... on InvalidSnippetParams {
          snippetsWithParams
          fieldName
        }
        ... on InvalidRokuFontUri {
          __typename
        }
        ... on InvalidImageURI {
          __typename
        }
        ... on InvalidRokuHexValue {
          __typename
        }
        ... on MissingSnippets {
          snippets
          fieldName
        }
        ... on MissingLink {
          __typename
        }
        ... on MissingMessageOrSilentPush {
          fieldName
        }
        ... on InvalidActionButtonUri {
          invalidButtonActions {
            type
            data
          }
        }
        ... on InvalidSnippetUsage {
          fieldName
          index
        }
        ... on InvalidHandlebarCount {
          fieldName
          index
        }
        ... on InvalidHandlebarComplexity {
          fieldName
          index
        }
        ... on InvalidWhatsAppButtonUrlFormat {
          index
        }
        ... on InvalidWhatsAppButtonUrlParams {
          index
        }
        ... on InvalidWhatsAppFooter {
          __typename
        }
        ... on EmptyWhatsAppMessage {
          __typename
        }
        ... on EmojiInButtonText {
          index
        }
        ... on WhatsAppEmojiLimitExceeded {
          __typename
        }
        ... on EmptyWhatsAppButtonText {
          index
        }
        ... on EmptyWhatsAppButtonPhoneNumber {
          index
        }
        ... on EmptyWhatsAppButtonUrl {
          index
        }
        ... on InvalidUrlFormat {
          __typename
        }
        ... on InvalidImageSize {
          __typename
        }
        ... on InvalidImageExtension {
          __typename
        }
      }
    }
  }
`;

export function useValidateContentQuery(
  options: QueryHookOptions<ValidateContentQueryResponse, ValidateContentQueryVariables>,
  query: DocumentNode = validateContentQuery,
): UseValidateContentQueryState {
  const primitiveOptions = JSON.stringify(options);
  const queryOptions = useMemo(() => {
    const _options = JSON.parse(primitiveOptions);
    return {
      notifyOnNetworkStatusChange: true,
      ..._options,
    };
  }, [primitiveOptions]);

  const {
    data,
    error: hasError,
    isLoading,
    refetch,
  } = useGraphql<ValidateContentQueryResponse, ValidateContentQueryVariables>(query, {
    fetchPolicy: 'no-cache',
    ...queryOptions,
  });

  if (isDefined<ValidateContentQueryResponse>(data)) {
    return {
      validationFailures: data?.validateContent?.errors || [],
      hasError,
      locale: data?.validateContent?.locale,
      isLoading,
      refetch,
    };
  }
  return {
    isLoading,
    hasError,
    refetch,
    locale: { name: '' },
    validationFailures: [],
  };
}
